import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { CONTACT_REQUIRED_FIELDS, type ContactFormField, type ValidationError } from '@wr/shared';
import { LISTS, marketLabel } from '../lists.js';
import { Checkbox, Field, ManyOfChips, OneOfChips, SelectField, TextAreaField } from './ui.js';

/**
 * The contact form (brief section 6.1), block 1 of every capture (decision
 * of Nov 2, 2026): name, email, company, job title and country, always
 * visible, in the same order everywhere; then what the screen puts in
 * between (Already a contact, Is there a project?, block 2); then More
 * info, optional and folded.
 */
export interface ContactDraft {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  job_title: string;
  mobile: string;
  office_phone: string;
  linkedin_url: string;
  country: string;
  applications: string[];
  interest: string;
  role: string;
  pain: string;
  wants_content: boolean;
  note: string;
}

export const EMPTY_CONTACT: ContactDraft = {
  first_name: '',
  last_name: '',
  email: '',
  company: '',
  job_title: '',
  mobile: '',
  office_phone: '',
  linkedin_url: '',
  country: '',
  applications: [],
  interest: '',
  role: '',
  pain: '',
  wants_content: false,
  note: '',
};

export type FieldErrors = Partial<Record<ContactFormField, string>>;

export function toFieldErrors(errors: readonly ValidationError[]): FieldErrors {
  return Object.fromEntries(errors.map((e) => [e.field, e.message]));
}

/** The id of a field's control, to move the focus to the first error. */
export const fieldId = (prefix: string, field: ContactFormField) => `${prefix}-${field}`;

export function ContactForm({
  value,
  onChange,
  errors,
  moreOpen,
  onMoreToggle,
  required = [],
  leadAnswers = true,
  children,
}: {
  value: ContactDraft;
  onChange(value: ContactDraft): void;
  errors: FieldErrors;
  moreOpen: boolean;
  onMoreToggle(open: boolean): void;
  /** Optional fields required by the current choice (a lead's five answers). */
  required?: readonly ContactFormField[];
  /**
   * The answers a contact may carry (applications, interest, role, pain) in
   * More info: shown when editing a contact, not when capturing one (block 2
   * asks them for a lead).
   */
  leadAnswers?: boolean;
  /** Between block 1 and More info. */
  children?: ReactNode;
}) {
  const prefix = useId();
  const set = <K extends keyof ContactDraft>(field: K, v: ContactDraft[K]) =>
    onChange({ ...value, [field]: v });
  const mark = (field: ContactFormField) =>
    (CONTACT_REQUIRED_FIELDS as readonly string[]).includes(field) || required.includes(field);
  const text = (
    field: ContactFormField & keyof ContactDraft,
    label: string,
    extra: InputHTMLAttributes<HTMLInputElement> & { hint?: string } = {},
  ) => (
    <Field
      id={fieldId(prefix, field)}
      label={label}
      mark={mark(field)}
      error={errors[field]}
      value={value[field] as string}
      onChange={(e) => set(field, e.target.value)}
      {...extra}
    />
  );

  return (
    <>
      <div className="g2">
        {text('first_name', 'First name', { autoComplete: 'off' })}
        {text('last_name', 'Last name', { autoComplete: 'off' })}
      </div>
      <div className="g2">
        {text('email', 'Email', { type: 'email', inputMode: 'email', autoComplete: 'off' })}
        {text('company', 'Company', { autoComplete: 'off' })}
      </div>
      <div className="g2">
        {text('job_title', 'Job title')}
        <SelectField
          id={fieldId(prefix, 'country')}
          label="Country"
          mark={mark('country')}
          error={errors.country}
          value={value.country}
          onChange={(e) => set('country', e.target.value)}
        >
          <option value="">Select a country</option>
          {LISTS.countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </SelectField>
      </div>
      {children}
      <details
        className="k-more"
        open={moreOpen}
        onToggle={(e) => onMoreToggle((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary>More info (optional)</summary>
        <div className="k-more-body">
          <div className="g2">
            {text('mobile', 'Mobile', { type: 'tel', inputMode: 'tel' })}
            {text('office_phone', 'Office phone', { type: 'tel', inputMode: 'tel' })}
          </div>
          {text('linkedin_url', 'LinkedIn', { type: 'url', inputMode: 'url' })}
          {leadAnswers && (
            <>
              <ManyOfChips
                id={fieldId(prefix, 'applications')}
                label="Applications"
                mark={mark('applications')}
                error={errors.applications}
                options={LISTS.markets.map((m) => ({ value: m.code, label: marketLabel(m.code) }))}
                value={value.applications}
                onChange={(v) => set('applications', v)}
              />
              <OneOfChips
                id={fieldId(prefix, 'interest')}
                label="Interest"
                mark={mark('interest')}
                error={errors.interest}
                options={LISTS.interestOptions}
                value={value.interest}
                onChange={(v) => set('interest', v)}
              />
              <OneOfChips
                id={fieldId(prefix, 'role')}
                label="Role"
                mark={mark('role')}
                error={errors.role}
                options={LISTS.roleOptions}
                value={value.role}
                onChange={(v) => set('role', v)}
              />
              {text('pain', 'Pain', { hint: 'What they sort, the most frustrating part' })}
            </>
          )}
          <Checkbox
            checked={value.wants_content}
            onChange={(e) => set('wants_content', e.target.checked)}
          >
            Send brochure and video (a HubSpot task is created)
          </Checkbox>
          <TextAreaField
            id={fieldId(prefix, 'note')}
            label="Note"
            rows={2}
            value={value.note}
            onChange={(e) => set('note', e.target.value)}
          />
        </div>
      </details>
    </>
  );
}
